/**
 * Concurrency Pool - Execute async tasks with controlled parallelism
 * 
 * Features:
 * - Configurable concurrency limit
 * - Retry logic with exponential backoff
 * - Cancellation via AbortController
 * - Progress tracking with throttled callbacks
 * - Error collection and reporting
 */

/**
 * Execute async tasks with concurrency limit
 * 
 * @param {Array} items - Array of items to process
 * @param {Function} workerFn - Async function to process each item: (item, index) => Promise<result>
 * @param {Object} options - Configuration options
 * @param {number} options.concurrency - Max number of concurrent tasks (default: 8)
 * @param {AbortSignal} options.signal - AbortSignal for cancellation
 * @param {Function} options.onProgress - Progress callback: ({ completed, total, item, result }) => void
 * @param {number} options.retries - Number of retry attempts for failed tasks (default: 2)
 * @param {number} options.retryDelay - Initial retry delay in ms (default: 1000)
 * @param {number} options.progressThrottle - Min ms between progress callbacks (default: 100)
 * @returns {Promise<{ results: Array, errors: Array }>}
 */
export async function concurrencyPool(items, workerFn, options = {}) {
  const {
    concurrency = 8,
    signal = null,
    onProgress = null,
    retries = 2,
    retryDelay = 1000,
    progressThrottle = 100,
  } = options;

  if (!items || items.length === 0) {
    return { results: [], errors: [] };
  }

  const total = items.length;
  const results = new Array(total);
  const errors = [];
  let completed = 0;
  let lastProgressTime = 0;

  // Throttled progress callback
  const reportProgress = (item, result, index) => {
    if (!onProgress) return;

    const now = Date.now();
    if (now - lastProgressTime >= progressThrottle) {
      lastProgressTime = now;
      onProgress({ completed, total, item, result, index });
    }
  };

  // Worker function with retry logic
  const processItem = async (item, index) => {
    // Check for cancellation before starting
    if (signal?.aborted) {
      throw new Error('Operation cancelled');
    }

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Execute the worker function
        const result = await workerFn(item, index);
        
        // Success - record result and report progress
        results[index] = result;
        completed++;
        reportProgress(item, result, index);
        
        return result;
      } catch (error) {
        lastError = error;

        // Check if cancellation caused the error
        if (signal?.aborted) {
          throw new Error('Operation cancelled');
        }

        // If this isn't the last attempt, wait before retrying
        if (attempt < retries) {
          const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Check for cancellation after delay
          if (signal?.aborted) {
            throw new Error('Operation cancelled');
          }
        }
      }
    }

    // All retries failed - record error
    errors.push({
      item,
      index,
      error: lastError,
    });

    // Mark as completed (even though it failed)
    completed++;
    reportProgress(item, null, index);

    // Return null for failed items
    return null;
  };

  // Create a pool of workers
  const executing = new Set();
  
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const promise = processItem(item, i);
    
    executing.add(promise);

    // When promise completes, remove it from executing set
    promise.finally(() => executing.delete(promise));

    // If we've reached concurrency limit, wait for one to complete
    if (executing.size >= concurrency) {
      await Promise.race(executing);
    }

    // Check for cancellation between items
    if (signal?.aborted) {
      break;
    }
  }

  // Wait for all remaining promises to complete
  await Promise.allSettled(executing);

  // Final progress callback
  if (onProgress) {
    onProgress({ completed, total, item: null, result: null, index: -1 });
  }

  return { results, errors };
}

/**
 * Download a file with retry logic
 * 
 * @param {string} url - URL to download
 * @param {Object} options - Fetch options and retry config
 * @returns {Promise<Blob>}
 */
export async function downloadWithRetry(url, options = {}) {
  const {
    signal = null,
    retries = 2,
    retryDelay = 1000,
    ...fetchOptions
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal,
      });

      if (!response.ok) {
        // Don't retry 4xx errors (except 429 Too Many Requests)
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      lastError = error;

      // Don't retry if cancelled
      if (signal?.aborted || error.name === 'AbortError') {
        throw new Error('Download cancelled');
      }

      // If this isn't the last attempt, wait before retrying
      if (attempt < retries) {
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

export default concurrencyPool;